import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { slideUp, EASE_OUT } from '@/lib/motion';
import PageHeader from '@/components/common/PageHeader';
import {
  UserCircleIcon,
  UsersIcon,
  BuildingOfficeIcon,
  CreditCardIcon,
  BellIcon,
  KeyIcon,
  ClipboardDocumentListIcon,
  LinkIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { usePermissions } from '../../hooks/usePermissions';
import { PERM, type Permission } from '../../auth/permissions';
import GeneralSettings from '../../components/settings/GeneralSettings';
import AuditLogSettings from '../../components/settings/AuditLogSettings';
import OrganizationPanel from '../../components/settings/OrganizationPanel';
import TeamMembers from '../../components/settings/TeamMembers';
import BillingAndPlanSettings from '../../components/settings/BillingAndPlanSettings';
import NotificationsSettings from '../../components/settings/NotificationsSettings';
import SecuritySettings from '../../components/settings/SecuritySettings';
import AISettings from '../../components/settings/AISettings';
import IntegrationsSettings from './IntegrationsSettings';

type SettingsTab =
  | 'general'
  | 'team'
  | 'organization'
  | 'billing'
  | 'ai'
  | 'notifications'
  | 'security'
  | 'integrations' | 'audit';

const tabs: Array<{
  id: SettingsTab;
  label: string;
  icon: React.ElementType;
  requirePerm?: Permission;
}> = [
  { id: 'general', label: 'General', icon: UserCircleIcon },
  { id: 'team', label: 'Team', icon: UsersIcon, requirePerm: PERM.TEAM_MANAGE },
  { id: 'organization', label: 'Organization', icon: BuildingOfficeIcon },
  { id: 'billing', label: 'Billing & Plan', icon: CreditCardIcon },
  { id: 'ai', label: 'AI', icon: SparklesIcon },
  { id: 'integrations', label: 'Integrations', icon: LinkIcon },
  { id: 'notifications', label: 'Notifications', icon: BellIcon },
  { id: 'security', label: 'Security', icon: KeyIcon },
  { id: 'audit', label: 'Audit Log', icon: ClipboardDocumentListIcon, requirePerm: PERM.TENANT_MANAGE },
];

const SettingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = usePermissions();
  const currentTab = (searchParams.get('tab') as SettingsTab) || 'general';
  const visibleTabs = tabs.filter((t) => !t.requirePerm || can(t.requirePerm));

  const handleTabChange = (tabId: SettingsTab) => {
    setSearchParams({ tab: tabId });
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case 'general': return <GeneralSettings />;
      case 'team': return can(PERM.TEAM_MANAGE) ? <TeamMembers /> : <GeneralSettings />;
      case 'organization': return <OrganizationPanel />;
      case 'billing': return <BillingAndPlanSettings />;
      case 'ai': return <AISettings />;
      case 'notifications': return <NotificationsSettings />;
      case 'security': return <SecuritySettings />;
      case 'integrations': return <IntegrationsSettings />;
      case 'audit': return <AuditLogSettings />;
      default: return <GeneralSettings />;
    }
  };

  return (
    <motion.div {...slideUp}>
      <PageHeader
        title="Settings"
        className="mb-8"
        trail={[{ label: 'Settings', to: '/settings' }]}
        leaf={tabs.find((t) => t.id === currentTab)?.label ?? 'General'}
      />

      {/* Tabs */}
      {/* overflow-y-hidden: the -mb-px nav hangs 1px below this scroll container;
          without it a dead 1px scrollbar renders next to the tabs. */}
      <div className="border-b border-border-primary/20 mb-8 overflow-x-auto overflow-y-hidden">
        <nav className="flex min-w-max -mb-px">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const active = currentTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  active
                    ? 'border-text-primary text-text-primary'
                    : 'border-transparent text-text-secondary hover:text-text-primary hover:border-border-primary/40'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content — keyed quick fade so switching tabs glides instead of snapping */}
      <motion.div
        key={currentTab}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.15, ease: EASE_OUT }}
      >
        {renderTabContent()}
      </motion.div>
    </motion.div>
  );
};

export default SettingsPage;
